import { Injectable } from '@angular/core'
import { from, BehaviorSubject } from 'rxjs'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { ProjectApiService } from 'src/app/builder-services/api/project.api.service'
import { LoginService } from 'src/app/login/login.service'

export type AddonStatus = 'AVAILABLE' | 'ACTIVE' | 'EXPIRED' | 'GRACE_PERIOD'

export interface GracePeriodInfo {
  daysRemaining: number
  expirationDate: Date
  addonName: string
  isInGracePeriod: boolean
}

@Injectable({
  providedIn: 'root'
})
export class AddonsAccessService {
  private subscriptions$ = new BehaviorSubject<any[]>([])
  private readonly GRACE_PERIOD_DAYS = 3

  constructor(private projectApi: ProjectApiService, private builderService: BuilderService,private loginService: LoginService) {}

  /** Load once per project */
  loadSubscriptions() {
    const projectId = this.builderService.selectedProject
    from(this.projectApi.getAddonSubscriptions(projectId)).subscribe({
      next: (addons) => this.subscriptions$.next(addons),
      error: () => this.subscriptions$.next([]),
    })
  }

  getSubscriptions$() {
    return this.subscriptions$.asObservable()
  }

  /** Core logic with grace period support */
  getAddonStatus(addonName: string): AddonStatus {
    // Admins bypass all addon restrictions
    if (this.loginService.getCurrentUser()?.admin) {
      return 'ACTIVE'
    }

    const subs = this.subscriptions$.value

    // 1️⃣ Bundle overrides everything
    const bundle = subs.find((s) => s.addon_details?.name === 'All Addons Bundle' && s.status === 'active')
    if (bundle) {
      return 'ACTIVE'
    }

    // 2️⃣ Find addon
    const addon = subs.find((s) => s.addon_details?.name === addonName)

    if (!addon || addon.status !== 'active') {
      return 'AVAILABLE'
    }

    // 3️⃣ Expiry check with grace period
    const end = addon.stripe_details?.current_period_end
    if (!end) return 'ACTIVE'

    const endDate = new Date(end.replace(' ', 'T'))
    const now = new Date()

    // Calculate days difference
    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // If expired, check grace period
    if (now.getTime() >= endDate.getTime()) {
      const daysSinceExpiry = Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))

      if (daysSinceExpiry < this.GRACE_PERIOD_DAYS) {
        return 'GRACE_PERIOD'
      }

      return 'EXPIRED'
    }

    return 'ACTIVE'
  }

  /** Get grace period information for an addon */
  getGracePeriodInfo(addonName: string): GracePeriodInfo | null {
    const subs = this.subscriptions$.value
    const addon = subs.find((s) => s.addon_details?.name === addonName)

    if (!addon) return null

    const end = addon.stripe_details?.current_period_end
    if (!end) return null

    const endDate = new Date(end.replace(' ', 'T'))
    const now = new Date()

    const diffTime = endDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // Check if in grace period
    if (now.getTime() >= endDate.getTime()) {
      const daysSinceExpiry = Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))

      const daysRemaining = this.GRACE_PERIOD_DAYS - daysSinceExpiry

      if (daysRemaining > 0) {
        return {
          daysRemaining,
          expirationDate: endDate,
          addonName,
          isInGracePeriod: true
        }
      }
    }

    return {
      daysRemaining: 0,
      expirationDate: endDate,
      addonName,
      isInGracePeriod: false
    }
  }

  isAddonActive(addonName: string): boolean {
    const status = this.getAddonStatus(addonName)
    // During grace period, addon is still functionally active
    // console.log(`Addon "${addonName}" status:`, status)
    return status === 'ACTIVE' || status === 'GRACE_PERIOD'
  }

  isAddonExpired(addonName: string): boolean {
    return this.getAddonStatus(addonName) === 'EXPIRED'
  }

  isAddonInGracePeriod(addonName: string): boolean {
    return this.getAddonStatus(addonName) === 'GRACE_PERIOD'
  }
}
